import { FAQ_CATEGORIES, FAQ_ENTRIES } from "./catalog";
import { resolveHelpFaqSurface } from "./resolveHelpFaqSurface";
import { FAQ_SURFACE_PRESETS } from "./surfaces";
import type { FaqCategory, FaqEntry, FaqSurface } from "./types";

const byId = new Map<string, FaqEntry>(
  FAQ_ENTRIES.map((entry) => [entry.id, entry]),
);

export function getFaqEntryById(id: string): FaqEntry | undefined {
  return byId.get(id);
}

export function getAllFaqEntries(): readonly FaqEntry[] {
  return FAQ_ENTRIES;
}

export function getFaqCategories(): readonly FaqCategory[] {
  return FAQ_CATEGORIES;
}

/** Resolve entries for a category in display order; skips unknown ids. */
export function getFaqEntriesForCategory(category: FaqCategory): FaqEntry[] {
  return category.entryIds
    .map((id) => byId.get(id))
    .filter((entry): entry is FaqEntry => entry != null);
}

export function selectFaqEntriesByIds(ids: readonly string[]): FaqEntry[] {
  const seen = new Set<string>();
  const out: FaqEntry[] = [];
  for (const id of ids) {
    if (seen.has(id)) continue;
    const entry = byId.get(id);
    if (!entry) continue;
    seen.add(id);
    out.push(entry);
  }
  return out;
}

export function selectFaqEntriesBySurface(surface: FaqSurface): FaqEntry[] {
  return selectFaqEntriesByIds(FAQ_SURFACE_PRESETS[surface]);
}

/**
 * Help FAB sheet entries — route-aware surface from pathname (D11).
 * Callers pass pathname only; id lists stay in `surfaces.ts`.
 */
export function selectFaqEntriesForHelp(pathname: string): FaqEntry[] {
  return selectFaqEntriesBySurface(resolveHelpFaqSurface(pathname));
}

/** First category index that contains the entry id, or -1. */
export function findFaqCategoryIndexForEntryId(entryId: string): number {
  return FAQ_CATEGORIES.findIndex((category) =>
    category.entryIds.includes(entryId),
  );
}

export function assertUniqueFaqEntryIds(): string[] {
  const seen = new Set<string>();
  const dupes: string[] = [];
  for (const entry of FAQ_ENTRIES) {
    if (seen.has(entry.id)) dupes.push(entry.id);
    seen.add(entry.id);
  }
  return dupes;
}

/** Every category entryId must resolve to a catalog entry. */
export function assertCategoryEntryIdsResolve(): string[] {
  const missing: string[] = [];
  for (const category of FAQ_CATEGORIES) {
    for (const id of category.entryIds) {
      if (!byId.has(id)) missing.push(`${category.id}:${id}`);
    }
  }
  return missing;
}
